package com.example.borshi.web.plans

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.example.borshi.web.Screen
import com.example.borshi.web.cart.CartStore
import com.example.borshi.web.cart.DeliverySlot
import com.example.borshi.web.cart.OrderFrequency
import com.example.borshi.web.cart.SubscriptionSchedule
import com.example.borshi.web.firebase.FirebaseService
import com.example.borshi.web.model.PlanMixItem
import com.example.borshi.web.model.Product
import com.example.borshi.web.model.ProteinPlan
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.border
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.bottom
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.cursor
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.flexWrap
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.left
import org.jetbrains.compose.web.css.lineHeight
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.maxHeight
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.overflow
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.paddingTop
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.right
import org.jetbrains.compose.web.css.textAlign
import org.jetbrains.compose.web.css.top
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.H5
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlinx.coroutines.launch
import kotlin.js.Date

private object DT {
    const val ink = "#0D2B2E"
    const val inkMid = "#5A6C6D"
    const val teal = "#26A69A"
    const val tealDeep = "#1A7A72"
    const val tealGhost = "#E0F2F1"
    const val crimson = "#D11243"
    const val white = "#FFFFFF"
    const val border = "#E0EDEB"
    const val surface = "#F5F9F8"
}

data class ConsultationSlot(
    val id: String,
    val date: String,
    val time: String,
    val displayLabel: String,
    val epochMs: Long
)

private fun generateAvailableSlots(): List<ConsultationSlot> {
    val now = Date.now().toLong()
    val dayMs = 86_400_000L
    val slots = mutableListOf<ConsultationSlot>()

    for (dayOffset in 1..7) {
        val dayEpoch = now + dayOffset * dayMs
        val d = Date(dayEpoch.toDouble())
        val dayNames = arrayOf("Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat")
        val monthNames = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
        val dayName = dayNames[d.getDay()]
        val monthName = monthNames[d.getMonth()]
        val dateNum = d.getDate()
        val dateStr = "$dayName, $monthName $dateNum"

        listOf("10:00 AM" to 10, "11:30 AM" to 11, "2:00 PM" to 14, "4:00 PM" to 16, "6:00 PM" to 18).forEach { (timeStr, hour) ->
            val slotEpoch = dayEpoch + (hour - 12) * 3600_000L
            slots.add(ConsultationSlot(
                id = "slot_${dayOffset}_$hour",
                date = dateStr,
                time = timeStr,
                displayLabel = "$dateStr · $timeStr",
                epochMs = slotEpoch
            ))
        }
    }
    return slots
}

@Composable
fun ProteinPlanDetailScreen(
    plan: ProteinPlan,
    onBack: () -> Unit,
    cartStore: CartStore? = null,
    onNavigate: ((Screen) -> Unit)? = null
) {
    var allProducts by remember { mutableStateOf<List<Product>>(emptyList()) }
    var favouriteItems by remember { mutableStateOf(plan.mixItems) }
    var isSelectingFavourites by remember { mutableStateOf(false) }
    var availableProducts by remember { mutableStateOf<List<Product>>(emptyList()) }
    var selectedSlot by remember { mutableStateOf<ConsultationSlot?>(null) }
    var showSlotPicker by remember { mutableStateOf(false) }

    val consultationSlots = remember { generateAvailableSlots() }
    val groupedSlots = remember(consultationSlots) { consultationSlots.groupBy { it.date } }

    fun addPlanToCart() {
        if (cartStore == null || onNavigate == null) return
        val schedule = SubscriptionSchedule(
            frequency = OrderFrequency.DAILY,
            startDate = Date.now().toLong(),
            endDate = null,
            slot = DeliverySlot.MORNING
        )

        if (favouriteItems.isEmpty()) {
            val planProduct = Product(
                id = plan.planId.ifBlank { plan.planName },
                name = plan.planName,
                price = plan.price,
                imageUrl = plan.imageUrl
            )
            cartStore.addSubscription(
                product = planProduct,
                packSize = plan.duration.ifBlank { "1 plan" },
                qtyPerDelivery = 1,
                schedule = schedule
            )
        } else {
            favouriteItems.forEach { mixItem ->
                val product = Product(
                    id = mixItem.productId,
                    name = mixItem.productName,
                    price = mixItem.productPrice,
                    imageUrl = mixItem.productImage
                )
                cartStore.addSubscription(
                    product = product,
                    packSize = mixItem.qty,
                    qtyPerDelivery = 1,
                    schedule = schedule
                )
            }
        }

        // Save appointment to Firestore if a consultation slot was selected
        if (selectedSlot != null && plan.consultationEligible) {
            val uid = FirebaseService.getCurrentUserId()
            if (uid != null) {
                val scope = kotlinx.coroutines.MainScope()
                val slot = selectedSlot!!
                val favIds = favouriteItems.map { it.productId }
                scope.launch {
                    try {
                        FirebaseService.saveAppointment(
                            planId = plan.planId,
                            planName = plan.planName,
                            nutritionistId = plan.nutritionistId,
                            nutritionistName = plan.nutritionistName,
                            slotId = slot.id,
                            slotLabel = slot.displayLabel,
                            slotEpoch = slot.epochMs,
                            favouriteItemIds = favIds
                        )
                    } catch (e: Throwable) {
                        console.warn("Failed to save appointment:", e)
                    }
                }
            }
        }
        onNavigate(Screen.Cart)
    }

    LaunchedEffect(Unit) {
        try {
            allProducts = FirebaseService.getProducts()
            availableProducts = allProducts
        } catch (e: Throwable) {
            console.error("Failed to load products:", e)
        }
    }

    Div({
        style {
            maxWidth(1200.px)
            property("margin", "0 auto")
            padding(24.px, 16.px)
            width(100.percent)
        }
    }) {
        // Back Button
        Button({
            style {
                backgroundColor(Color("transparent"))
                color(Color(DT.ink))
                border(2.px, LineStyle.Solid, Color(DT.border))
                padding(10.px, 20.px)
                borderRadius(8.px)
                cursor("pointer")
                fontSize(14.px)
                marginBottom(24.px)
            }
            onClick { onBack() }
        }) {
            Text("← Back to Plans")
        }

        // Plan Header
        Div({
            style {
                backgroundColor(Color.white)
                borderRadius(24.px)
                property("box-shadow", "0 4px 20px rgba(13, 43, 46, 0.08)")
                marginBottom(32.px)
                overflow("hidden")
            }
        }) {
            Div({
                style {
                    height(300.px)
                    backgroundColor(Color("#F5F5F5"))
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    overflow("hidden")
                }
            }) {
                val imgUrl = plan.imageUrl.takeIf { it.isNotBlank() && it.startsWith("http") }
                    ?: FirebaseService.getPlanImageUrl(com.example.borshi.web.model.PlanCategory.GENERAL_HEALTH)
                Img(src = imgUrl, alt = plan.planName, attrs = {
                    style {
                        width(100.percent)
                        height(100.percent)
                        property("object-fit", "cover")
                    }
                })
            }

            Div({ style { padding(40.px) } }) {
                H1({
                    style {
                        fontSize(36.px); fontWeight("700")
                        marginBottom(8.px); color(Color(DT.ink))
                    }
                }) { Text(plan.planName) }

                // Nutritionist attribution
                if (plan.nutritionistName.isNotBlank()) {
                    Div({
                        style {
                            display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px)
                            marginBottom(16.px)
                        }
                    }) {
                        Span({ style { fontSize(16.px) } }) { Text("👨‍⚕️") }
                        Span({
                            style {
                                fontSize(15.px); fontWeight("600"); color(Color(DT.teal))
                            }
                        }) { Text("by ${plan.nutritionistName}") }
                    }
                }

                P({
                    style {
                        fontSize(18.px); color(Color(DT.inkMid))
                        marginBottom(24.px); lineHeight("1.6")
                    }
                }) { Text(plan.description) }

                Div({
                    style {
                        display(DisplayStyle.Flex); gap(32.px); flexWrap(FlexWrap.Wrap)
                    }
                }) {
                    InfoBadge("📅 ${plan.duration}", "Duration")
                    InfoBadge("🚚 ${plan.deliveriesPerWeek}x/week", "Delivery")
                    InfoBadge("₹${plan.price.toInt()}", "Price")
                    if (plan.consultationEligible) {
                        InfoBadge("👨‍⚕️ Included", "Consultation")
                    }
                }

                // Consultation info banner
                if (plan.consultationEligible) {
                    Div({
                        style {
                            marginTop(24.px)
                            backgroundColor(Color(DT.tealGhost))
                            border(1.px, LineStyle.Solid, Color(DT.teal))
                            borderRadius(12.px)
                            padding(16.px, 20.px)
                        }
                    }) {
                        Div({
                            style {
                                fontSize(14.px); fontWeight("600"); color(Color(DT.tealDeep))
                                marginBottom(4.px)
                            }
                        }) { Text("🩺 How it works") }
                        P({
                            style {
                                fontSize(13.px); color(Color(DT.inkMid)); lineHeight("1.6")
                            }
                        }) {
                            Text("1. Select your favourite items below — these are your preferences, not the final mix. ")
                            Text("2. Pick a consultation slot. ")
                            Text("3. During the consultation, our nutritionist will create your final personalised mix based on your health goals and preferences.")
                        }
                    }
                }

                // Top action buttons
                if (cartStore != null && onNavigate != null) {
                    Div({
                        style {
                            marginTop(24.px)
                            display(DisplayStyle.Flex)
                            gap(12.px)
                            flexWrap(FlexWrap.Wrap)
                            alignItems(AlignItems.Center)
                        }
                    }) {
                        // Slot picker button (for consultation plans)
                        if (plan.consultationEligible) {
                            Button({
                                style {
                                    backgroundColor(if (selectedSlot != null) Color(DT.tealGhost) else Color(DT.white))
                                    color(Color(DT.teal))
                                    padding(14.px, 24.px)
                                    borderRadius(12.px)
                                    border(2.px, LineStyle.Solid, Color(DT.teal))
                                    cursor("pointer")
                                    fontWeight("700")
                                    fontSize(14.px)
                                }
                                onClick { showSlotPicker = !showSlotPicker }
                            }) {
                                Text(selectedSlot?.let { "📅 ${it.displayLabel}" } ?: "📅 Pick Consultation Slot")
                            }
                        }

                        Button({
                            style {
                                backgroundColor(Color(DT.teal))
                                color(Color.white)
                                padding(14.px, 32.px)
                                borderRadius(12.px)
                                border(0.px)
                                cursor("pointer")
                                fontWeight("700")
                                fontSize(16.px)
                            }
                            onClick { addPlanToCart() }
                        }) {
                            Text("🛒 Add Plan to Cart")
                        }
                    }
                }

                // Slot picker dropdown
                if (showSlotPicker && plan.consultationEligible) {
                    Div({
                        style {
                            marginTop(16.px)
                            backgroundColor(Color.white)
                            border(1.5.px, LineStyle.Solid, Color(DT.border))
                            borderRadius(16.px)
                            padding(20.px)
                            property("box-shadow", "0 4px 20px rgba(13,43,46,0.1)")
                            maxHeight(400.px)
                            overflow("auto")
                        }
                    }) {
                        H3({
                            style {
                                fontSize(16.px); fontWeight("700"); color(Color(DT.ink))
                                marginBottom(16.px)
                            }
                        }) { Text("Choose a consultation slot") }

                        groupedSlots.forEach { (dateLabel, slots) ->
                            Div({ style { marginBottom(16.px) } }) {
                                Div({
                                    style {
                                        fontSize(13.px); fontWeight("700"); color(Color(DT.tealDeep))
                                        marginBottom(8.px); property("letter-spacing", "0.03em")
                                    }
                                }) { Text(dateLabel) }

                                Div({
                                    style {
                                        display(DisplayStyle.Flex); gap(8.px)
                                        flexWrap(FlexWrap.Wrap)
                                    }
                                }) {
                                    slots.forEach { slot ->
                                        val isSelected = selectedSlot?.id == slot.id
                                        Button({
                                            style {
                                                padding(8.px, 16.px)
                                                borderRadius(999.px)
                                                fontSize(13.px); fontWeight("600")
                                                cursor("pointer")
                                                border(1.5.px, LineStyle.Solid, if (isSelected) Color(DT.teal) else Color(DT.border))
                                                backgroundColor(if (isSelected) Color(DT.teal) else Color.white)
                                                color(if (isSelected) Color.white else Color(DT.inkMid))
                                                property("transition", "all 0.15s ease")
                                            }
                                            onClick {
                                                selectedSlot = slot
                                                showSlotPicker = false
                                            }
                                        }) { Text(slot.time) }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // Favourite Items Section
        Div({
            style {
                backgroundColor(Color.white)
                padding(32.px)
                borderRadius(24.px)
                property("box-shadow", "0 4px 20px rgba(13, 43, 46, 0.08)")
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    marginBottom(8.px)
                }
            }) {
                H2({
                    style {
                        fontSize(24.px); fontWeight("700"); color(Color(DT.ink))
                    }
                }) { Text("Select Your Favourite Items") }

                Button({
                    style {
                        backgroundColor(if (isSelectingFavourites) Color("#FF7043") else Color(DT.teal))
                        color(Color.white)
                        padding(12.px, 24.px)
                        borderRadius(999.px)
                        border(0.px)
                        cursor("pointer")
                        fontWeight("700")
                        fontSize(14.px)
                    }
                    onClick { isSelectingFavourites = !isSelectingFavourites }
                }) {
                    Text(if (isSelectingFavourites) "✓ Done Selecting" else "❤️ Choose Favourites")
                }
            }

            P({
                style {
                    fontSize(14.px); color(Color(DT.inkMid)); marginBottom(24.px); lineHeight("1.6")
                }
            }) {
                Text("Pick the items you love — your nutritionist will use these preferences to craft your personalised mix during the consultation.")
            }

            // Selected favourites grid
            if (favouriteItems.isNotEmpty()) {
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(auto-fill, minmax(250px, 1fr))")
                        gap(20.px)
                        marginBottom(if (isSelectingFavourites) 32.px else 0.px)
                    }
                }) {
                    favouriteItems.forEach { mixItem ->
                        MixItemCard(
                            mixItem = mixItem,
                            isCustomizing = isSelectingFavourites,
                            onRemove = {
                                favouriteItems = favouriteItems.filter { it.productId != mixItem.productId }
                            }
                        )
                    }
                }
            } else if (!isSelectingFavourites) {
                Div({
                    style {
                        textAlign("center")
                        padding(40.px, 0.px)
                        color(Color(DT.inkMid))
                    }
                }) {
                    Div({ style { fontSize(40.px); marginBottom(12.px) } }) { Text("❤️") }
                    P({ style { fontSize(15.px); fontWeight("500") } }) {
                        Text("No favourites selected yet. Tap 'Choose Favourites' to pick items you'd like in your plan.")
                    }
                }
            }

            // Add more products panel
            if (isSelectingFavourites) {
                Div({
                    style {
                        marginTop(32.px)
                        paddingTop(32.px)
                        property("border-top", "2px dashed ${DT.border}")
                    }
                }) {
                    H3({
                        style {
                            fontSize(20.px); fontWeight("700")
                            marginBottom(16.px); color(Color(DT.ink))
                        }
                    }) { Text("Browse & Add to Favourites") }

                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "repeat(auto-fill, minmax(200px, 1fr))")
                            gap(16.px)
                        }
                    }) {
                        availableProducts
                            .filter { product -> favouriteItems.none { it.productName == product.name } }
                            .forEach { product ->
                                AddProductCard(
                                    product = product,
                                    onAdd = {
                                        favouriteItems = favouriteItems + PlanMixItem(
                                            productId = product.name,
                                            qty = "500g",
                                            productName = product.name,
                                            productImage = product.imageUrl,
                                            productPrice = product.price
                                        )
                                    }
                                )
                            }
                    }
                }
            }
        }

        // Sticky bottom bar
        if (cartStore != null && onNavigate != null) {
            Div({
                style {
                    position(Position.Fixed)
                    bottom(0.px); left(0.px); right(0.px)
                    backgroundColor(Color.white)
                    padding(16.px)
                    property("box-shadow", "0 -4px 12px rgba(0,0,0,0.1)")
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                    gap(16.px)
                    property("z-index", "100")
                    flexWrap(FlexWrap.Wrap)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(12.px)
                    }
                }) {
                    Span({
                        style { fontSize(20.px); fontWeight("700"); color(Color(DT.crimson)) }
                    }) { Text("₹${plan.price.toInt()}") }
                    Span({
                        style { fontSize(12.px); color(Color("#666")) }
                    }) { Text("/ ${plan.duration}") }
                }

                if (favouriteItems.isNotEmpty()) {
                    Span({
                        style {
                            fontSize(12.px); fontWeight("600"); color(Color(DT.tealDeep))
                            backgroundColor(Color(DT.tealGhost))
                            padding(4.px, 12.px); borderRadius(999.px)
                        }
                    }) { Text("❤️ ${favouriteItems.size} favourite${if (favouriteItems.size != 1) "s" else ""}") }
                }

                if (selectedSlot != null) {
                    Span({
                        style {
                            fontSize(12.px); fontWeight("600"); color(Color(DT.tealDeep))
                            backgroundColor(Color(DT.tealGhost))
                            padding(4.px, 12.px); borderRadius(999.px)
                        }
                    }) { Text("📅 ${selectedSlot!!.time}") }
                }

                // Slot picker in sticky bar if consultation eligible
                if (plan.consultationEligible && selectedSlot == null) {
                    Button({
                        style {
                            backgroundColor(Color(DT.white))
                            color(Color(DT.teal))
                            padding(10.px, 16.px)
                            borderRadius(999.px)
                            border(1.5.px, LineStyle.Solid, Color(DT.teal))
                            cursor("pointer")
                            fontWeight("600"); fontSize(12.px)
                        }
                        onClick {
                            kotlinx.browser.document.getElementById("plan-header")
                                ?.scrollIntoView(js("({behavior:'smooth',block:'start'})"))
                            showSlotPicker = true
                        }
                    }) { Text("📅 Pick Slot") }
                }

                Button({
                    style {
                        backgroundColor(Color(DT.teal))
                        color(Color.white)
                        padding(14.px, 40.px)
                        borderRadius(999.px)
                        border(0.px)
                        cursor("pointer")
                        fontWeight("700")
                        fontSize(16.px)
                        property("box-shadow", "0 4px 16px rgba(38, 166, 154, 0.3)")
                    }
                    onClick { addPlanToCart() }
                }) { Text("🛒 Add Plan to Cart") }
            }
        }
    }
}

@Composable
fun InfoBadge(value: String, label: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(4.px)
        }
    }) {
        Div({
            style { fontSize(16.px); fontWeight("700"); color(Color(DT.ink)) }
        }) { Text(value) }
        Div({
            style {
                fontSize(12.px); color(Color(DT.inkMid))
                property("text-transform", "uppercase")
                property("letter-spacing", "0.05em")
            }
        }) { Text(label) }
    }
}

@Composable
fun MixItemCard(mixItem: PlanMixItem, isCustomizing: Boolean, onRemove: () -> Unit) {
    Div({
        style {
            backgroundColor(Color("#F9F9F9"))
            borderRadius(16.px)
            overflow("hidden")
            position(Position.Relative)
            property("transition", "all 0.3s ease")
        }
    }) {
        if (isCustomizing) {
            Button({
                style {
                    position(Position.Absolute)
                    top(8.px); right(8.px)
                    backgroundColor(Color("#FF4444"))
                    color(Color.white)
                    border(0.px)
                    borderRadius(50.percent)
                    width(32.px); height(32.px)
                    cursor("pointer"); fontSize(18.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    property("z-index", "10")
                }
                onClick { onRemove() }
            }) { Text("×") }
        }

        // Favourite heart badge
        if (!isCustomizing) {
            Div({
                style {
                    position(Position.Absolute)
                    top(8.px); left(8.px)
                    backgroundColor(Color("#FFEBEE"))
                    color(Color(DT.crimson))
                    borderRadius(999.px)
                    padding(4.px, 10.px)
                    fontSize(11.px); fontWeight("700")
                    property("z-index", "10")
                }
            }) { Text("❤️ Favourite") }
        }

        Div({
            style {
                height(150.px)
                backgroundColor(Color("#E0E0E0"))
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
            }
        }) {
            if (mixItem.productImage.isNotEmpty()) {
                Img(src = mixItem.productImage, attrs = {
                    style {
                        width(100.percent); height(100.percent)
                        property("object-fit", "cover")
                    }
                })
            } else {
                Div({ style { fontSize(48.px) } }) { Text("🥩") }
            }
        }

        Div({ style { padding(16.px) } }) {
            H4({
                style {
                    fontSize(16.px); fontWeight("700")
                    marginBottom(4.px); color(Color(DT.ink))
                }
            }) { Text(mixItem.productName) }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    marginTop(8.px)
                }
            }) {
                Span({
                    style { fontSize(14.px); color(Color(DT.teal)); fontWeight("600") }
                }) { Text(mixItem.qty) }

                if (mixItem.productPrice > 0) {
                    Span({
                        style { fontSize(14.px); color(Color(DT.inkMid)) }
                    }) { Text("₹${mixItem.productPrice.toInt()}") }
                }
            }
        }
    }
}

@Composable
fun AddProductCard(product: Product, onAdd: () -> Unit) {
    Div({
        style {
            backgroundColor(Color.white)
            border(2.px, LineStyle.Dashed, Color(DT.border))
            borderRadius(12.px)
            padding(16.px)
            textAlign("center")
            cursor("pointer")
            property("transition", "all 0.3s ease")
        }
        onClick { onAdd() }
    }) {
        Div({
            style {
                height(100.px); marginBottom(12.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
            }
        }) {
            if (product.imageUrl.isNotEmpty()) {
                Img(src = product.imageUrl, attrs = {
                    style {
                        maxHeight(100.px); maxWidth(100.px)
                        property("object-fit", "contain")
                    }
                })
            }
        }

        H5({
            style { fontSize(14.px); fontWeight("700"); marginBottom(4.px); color(Color(DT.ink)) }
        }) { Text(product.name) }

        Span({
            style { fontSize(12.px); color(Color(DT.inkMid)) }
        }) { Text("₹${product.price.toInt()}") }

        Div({
            style {
                marginTop(12.px)
                backgroundColor(Color(DT.teal))
                color(Color.white)
                padding(8.px)
                borderRadius(8.px)
                fontSize(12.px); fontWeight("700")
            }
        }) { Text("❤️ Add to Favourites") }
    }
}
